import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { callListTypeLabels, callListTypes, type CallListType } from '@brimo/shared';
import { Banner, Button, Dialog, RadioCard, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { createCampaign, createList, type EventTypeRef } from '../api';
import { EventTypeCombobox } from '../EventTypeCombobox';
import styles from './lists.module.css';

const TYPE_HINTS: Record<CallListType, string> = {
  smart: 'Folgt Filtern, Bestand ändert sich automatisch',
  manual: 'Kontakte werden von Hand hinzugefügt und entfernt',
  campaign: 'Füllt sich automatisch über Webhook oder API',
  import: 'Aus Excel importieren — alte Leads, ohne die Kontaktliste zu füllen',
};

/**
 * „Liste anlegen" (Terminierung 04): type, name and the mandatory event type; a campaign brings its list. The type
 * „Importliste" hands name and event type over to the three-step import dialog (D-238).
 */
export function CreateListDialog({
  open,
  onClose,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  onImport: (prefill: { name: string; eventType: EventTypeRef | null }) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [type, setType] = useState<CallListType>('smart');
  const [name, setName] = useState('');
  const [eventType, setEventType] = useState<EventTypeRef | null>(null);
  const [errors, setErrors] = useState<{ name?: string | undefined; eventType?: string | undefined }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function close() {
    setErrors({});
    setBanner(null);
    onClose();
  }

  async function save() {
    if (type === 'import') {
      onImport({ name: name.trim(), eventType });
      setName('');
      setEventType(null);
      setType('smart');
      close();
      return;
    }
    const next: { name?: string | undefined; eventType?: string | undefined } = {};
    if (name.trim() === '') next.name = 'Pflichtangabe.';
    if (eventType === null) next.eventType = 'Bitte wählen Sie einen Ereignistyp.';
    setErrors(next);
    if (next.name || next.eventType || eventType === null) return;
    setPending(true);
    setBanner(null);
    try {
      let listId: string | null;
      if (type === 'campaign') {
        const campaign = await createCampaign({ name: name.trim(), eventTypeId: eventType.id });
        listId = campaign.listId;
      } else {
        const list = await createList({ type, name: name.trim(), eventTypeId: eventType.id });
        listId = list.id;
      }
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
      setName('');
      setEventType(null);
      close();
      if (listId) void navigate(`/dialer/lists/${listId}`);
    } catch (caught) {
      if (caught instanceof ApiError && Object.keys(caught.fields).length > 0) {
        setErrors({ name: caught.fields['name'], eventType: caught.fields['eventTypeId'] });
      } else setBanner(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow="Kontakte · Listen"
      title="Liste anlegen"
      width={600}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void save()}>
            {type === 'import' ? 'Weiter zur Datei' : 'Liste anlegen'}
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <fieldset className={styles.dialogFieldset}>
          <legend className={styles.dialogLabel}>Art</legend>
          <div className={styles.typeChoices}>
            {callListTypes.map((value) => (
              <RadioCard
                key={value}
                name="dialer-list-type"
                title={callListTypeLabels[value]}
                description={TYPE_HINTS[value]}
                checked={type === value}
                onChange={() => setType(value)}
              />
            ))}
          </div>
        </fieldset>
        <TextField
          label="Name"
          required
          value={name}
          error={errors.name}
          onChange={(event) => setName(event.target.value)}
        />
        <div className={styles.dialogField}>
          <span className={styles.dialogLabel}>
            Ereignistyp für Termine aus dieser Liste<span className={styles.required}> *</span>
          </span>
          <EventTypeCombobox
            contactId={null}
            value={eventType}
            onChange={setEventType}
            label="Ereignistyp für Termine aus dieser Liste"
            invalid={errors.eventType !== undefined}
          />
          <span className={errors.eventType ? styles.dialogError : styles.dialogHint}>
            {errors.eventType ??
              'Sucht in allen Ereignissen aus Beratung, auch selbst angelegten. Dauer, Ort und Closer kommen aus dem Ereignis.'}
          </span>
        </div>
      </div>
    </Dialog>
  );
}
